import { Component } from 'react';
import {
    Badge,
    Button,
    Dialog,
    IconButton,
    Pane,
    Spinner,
    Text,
    Tab,
    Tablist,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronDownIcon,
    AddIcon,
    RefreshIcon,
    CloudDownloadIcon
} from 'evergreen-ui';

const CATALOG_IMAGE_BUCKET = process.env.REACT_APP_CATALOG_IMAGE_BUCKET || 'https://krit-content.ams3.cdn.digitaloceanspaces.com/catalog-images';

// JSON Tree View Component
class JsonTreeNode extends Component {
    constructor(props) {
        super(props);
        this.state = { expanded: props.defaultExpanded || false };
    }

    toggle = () => {
        this.setState({ expanded: !this.state.expanded });
    };

    renderValue(value, key) {
        if (value === null) {
            return <Text color="muted" fontStyle="italic">null</Text>;
        }
        if (value === undefined) {
            return <Text color="muted" fontStyle="italic">undefined</Text>;
        }
        if (typeof value === 'boolean') {
            return <Text color={value ? 'green' : 'red'}>{value.toString()}</Text>;
        }
        if (typeof value === 'number') {
            return <Text color="blue">{value}</Text>;
        }
        if (typeof value === 'string') {
            if (value.length > 100) {
                return <Text color="orange" title={value}>"{value.substring(0, 100)}..."</Text>;
            }
            return <Text color="orange">"{value}"</Text>;
        }
        if (Array.isArray(value)) {
            return <JsonTreeNode nodeKey={key} value={value} isArray defaultExpanded={false} />;
        }
        if (typeof value === 'object') {
            return <JsonTreeNode nodeKey={key} value={value} defaultExpanded={false} />;
        }
        return <Text>{String(value)}</Text>;
    }

    render() {
        const { nodeKey, value, isArray } = this.props;
        const { expanded } = this.state;

        const isExpandable = (typeof value === 'object' && value !== null);
        const entries = isExpandable ? Object.entries(value) : [];
        const isEmpty = entries.length === 0;

        if (!isExpandable) {
            return this.renderValue(value, nodeKey);
        }

        const bracketOpen = isArray ? '[' : '{';
        const bracketClose = isArray ? ']' : '}';

        return (
            <Pane>
                <Pane 
                    display="flex" 
                    alignItems="center" 
                    cursor={isEmpty ? 'default' : 'pointer'}
                    onClick={isEmpty ? undefined : this.toggle}
                    style={{ userSelect: 'none' }}
                >
                    {!isEmpty && (
                        <ChevronLeftIcon 
                            size={12} 
                            marginRight={4}
                            style={{ 
                                transform: expanded ? 'rotate(-90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.1s'
                            }}
                        />
                    )}
                    {isEmpty && <Pane width={16} />}
                    <Text color="muted" fontSize={12}>
                        {bracketOpen}
                        {!expanded && !isEmpty && <Text color="muted">...</Text>}
                        {(isEmpty || !expanded) && bracketClose}
                        {!isEmpty && <Text color="muted" marginLeft={4} fontSize={11}>({entries.length} {isArray ? 'items' : 'keys'})</Text>}
                    </Text>
                </Pane>
                {expanded && !isEmpty && (
                    <Pane marginLeft={16} borderLeft="1px solid #E4E7EB" paddingLeft={8}>
                        {entries.map(([key, val], idx) => (
                            <Pane key={key} display="flex" alignItems="flex-start" marginY={2}>
                                <Text fontWeight={500} color="purple" marginRight={4} fontSize={12}>
                                    {isArray ? `[${key}]` : key}:
                                </Text>
                                {this.renderValue(val, key)}
                            </Pane>
                        ))}
                        <Text color="muted" fontSize={12}>{bracketClose}</Text>
                    </Pane>
                )}
            </Pane>
        );
    }
}

/**
 * Catalog Detail Modal Component
 * Displays catalog item details with tabs for info, raw data, and images
 * 
 * Props:
 * - isShown: boolean - Whether the modal is visible
 * - item: object - The catalog item to display
 * - onClose: function - Callback when modal is closed
 * - onCreateProduct: function - Optional callback for create product button (if not provided, button is hidden)
 * - showCreateButton: boolean - Whether to show the create product button (default: true)
 * - onRefresh: function - Optional callback to refresh catalog item data
 * - refreshing: boolean - Whether data is currently being refreshed
 * - onSyncImages: function - Optional callback to sync images for the catalog item
 * - syncing: boolean - Whether images are currently being synced
 */
export default class CatalogDetailModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            activeTab: 0,
            imageIndex: 0,
            // Cache synced images to prevent recalculation on every render
            syncedImages: this.computeSyncedImages(props.item)
        };
    }

    computeSyncedImages = (item) => {
        if (!item?.images || item.images.length === 0) return [];
        return item.images.filter(img => img.synced === 1);
    };

    hasUnsyncedImages = (item) => {
        if (!item?.images || item.images.length === 0) return false;
        return item.images.some(img => img.synced === 0);
    };

    componentDidUpdate(prevProps) {
        // Reset state and recompute images when item changes
        if (prevProps.item?.id !== this.props.item?.id) {
            this.setState({ 
                activeTab: 0, 
                imageIndex: 0,
                syncedImages: this.computeSyncedImages(this.props.item)
            });
        }
        // Update synced images if the item's images array changed (e.g., after refresh)
        else if (prevProps.item?.images !== this.props.item?.images) {
            const newSyncedImages = this.computeSyncedImages(this.props.item);
            const currentIndex = this.state.imageIndex;
            this.setState({ 
                syncedImages: newSyncedImages,
                // Reset image index if it's out of bounds
                imageIndex: currentIndex >= newSyncedImages.length ? 0 : currentIndex
            });
        }
    }

    getTypeBadgeColor = (type) => {
        switch (type) {
            case 'product': return 'blue';
            case 'tcg_single': return 'purple';
            case 'service': return 'green';
            default: return 'neutral';
        }
    };

    getSourceBadgeColor = (source) => {
        switch (source) {
            case 'heo': return 'teal';
            case 'manual': return 'neutral';
            default: return 'neutral';
        }
    };

    render() {
        const { isShown, item, onClose, onCreateProduct, showCreateButton = true, onRefresh, refreshing = false, onSyncImages, syncing = false } = this.props;
        const { activeTab, imageIndex, syncedImages } = this.state;

        if (!item) return null;

        const tabs = ['Informații', 'Date Raw', 'Imagini'];
        const hasUnsynced = this.hasUnsyncedImages(item);

        return (
            <Dialog
                isShown={isShown}
                title={item.name}
                onCloseComplete={onClose}
                hasFooter={true}
                width={900}
                footer={
                    <Pane display="flex" justifyContent="space-between" width="100%">
                        <Pane display="flex" gap={8}>
                            <Button onClick={onClose}>
                                Închide
                            </Button>
                            {onRefresh && (
                                <Button 
                                    iconBefore={refreshing ? null : RefreshIcon}
                                    onClick={onRefresh}
                                    disabled={refreshing || syncing}
                                >
                                    {refreshing ? <Spinner size={16} /> : 'Actualizează'}
                                </Button>
                            )}
                            {onSyncImages && hasUnsynced && (
                                <Button 
                                    iconBefore={syncing ? null : CloudDownloadIcon}
                                    onClick={onSyncImages}
                                    disabled={syncing || refreshing}
                                    intent="warning"
                                >
                                    {syncing ? <Spinner size={16} /> : 'Sincronizează Imagini'}
                                </Button>
                            )}
                        </Pane>
                        {showCreateButton && onCreateProduct && (
                            <Button 
                                appearance="primary"
                                iconBefore={AddIcon}
                                onClick={onCreateProduct}
                            >
                                Creează Produs
                            </Button>
                        )}
                    </Pane>
                }
            >
                <Tablist marginBottom={16}>
                    {tabs.map((tab, index) => (
                        <Tab
                            key={tab}
                            isSelected={activeTab === index}
                            onSelect={() => this.setState({ activeTab: index })}
                        >
                            {tab} {index === 2 && `(${syncedImages.length})`}
                        </Tab>
                    ))}
                </Tablist>

                {/* Tab 1: Catalog Info */}
                {activeTab === 0 && (
                    <Pane>
                        <Pane display="grid" gridTemplateColumns="150px 1fr" gap={10}>
                            <Text fontWeight="bold">ID:</Text>
                            <Text>{item.id}</Text>
                            
                            <Text fontWeight="bold">Item ID:</Text>
                            <Text>{item.itemId || '-'}</Text>
                            
                            <Text fontWeight="bold">Nume:</Text>
                            <Text>{item.name}</Text>
                            
                            <Text fontWeight="bold">Descriere:</Text>
                            <Text>{item.description || '-'}</Text>
                            
                            <Text fontWeight="bold">Tip:</Text>
                            <Badge color={this.getTypeBadgeColor(item.type)}>{item.type}</Badge>
                            
                            <Text fontWeight="bold">Sursă:</Text>
                            <Badge color={this.getSourceBadgeColor(item.source)}>{item.source}</Badge>
                            
                            <Text fontWeight="bold">GTIN:</Text>
                            <Text>{item.gtin || '-'}</Text>
                            
                            <Text fontWeight="bold">Thumbnail:</Text>
                            <Text>{item.thumbnail || '-'}</Text>
                            
                            <Text fontWeight="bold">Imagini totale:</Text>
                            <Text>{item.images?.length || 0}</Text>
                            
                            <Text fontWeight="bold">Imagini sincronizate:</Text>
                            <Text>{syncedImages.length}</Text>
                        </Pane>
                    </Pane>
                )}

                {/* Tab 2: Raw Data */}
                {activeTab === 1 && (
                    <Pane
                        background="tint1"
                        padding={16}
                        borderRadius={4}
                        maxHeight={500}
                        overflow="auto"
                    >
                        <JsonTreeNode 
                            nodeKey="rawData" 
                            value={(() => {
                                try {
                                    // Parse if it's a string, otherwise use as-is
                                    return typeof item.rawData === 'string' 
                                        ? JSON.parse(item.rawData) 
                                        : item.rawData;
                                } catch (e) {
                                    return item.rawData;
                                }
                            })()} 
                            defaultExpanded={true}
                        />
                    </Pane>
                )}

                {/* Tab 3: Image Slider */}
                {activeTab === 2 && (
                    <Pane>
                        {syncedImages.length === 0 ? (
                            <Pane 
                                display="flex" 
                                alignItems="center" 
                                justifyContent="center" 
                                height={300}
                                background="tint1"
                                borderRadius={4}
                            >
                                <Text color="muted">Nu există imagini sincronizate</Text>
                            </Pane>
                        ) : (
                            <Pane>
                                <Pane 
                                    display="flex" 
                                    alignItems="center" 
                                    justifyContent="center"
                                    position="relative"
                                    minHeight={400}
                                >
                                    <IconButton
                                        icon={ChevronLeftIcon}
                                        appearance="minimal"
                                        position="absolute"
                                        left={0}
                                        disabled={imageIndex === 0}
                                        onClick={() => this.setState({ imageIndex: imageIndex - 1 })}
                                    />
                                    
                                    <img
                                        src={`${CATALOG_IMAGE_BUCKET}/default_${syncedImages[imageIndex].imageUrl}.jpg`}
                                        alt={`Image ${imageIndex + 1}`}
                                        style={{
                                            maxWidth: '80%',
                                            maxHeight: '400px',
                                            objectFit: 'contain'
                                        }}
                                    />
                                    
                                    <IconButton
                                        icon={ChevronRightIcon}
                                        appearance="minimal"
                                        position="absolute"
                                        right={0}
                                        disabled={imageIndex >= syncedImages.length - 1}
                                        onClick={() => this.setState({ imageIndex: imageIndex + 1 })}
                                    />
                                </Pane>
                                
                                <Text display="block" textAlign="center" marginTop={10} color="muted">
                                    {imageIndex + 1} / {syncedImages.length}
                                </Text>
                                
                                {/* Thumbnail strip */}
                                <Pane 
                                    display="flex" 
                                    justifyContent="center" 
                                    gap={8} 
                                    marginTop={16}
                                    flexWrap="wrap"
                                >
                                    {syncedImages.map((img, idx) => (
                                        <img
                                            key={img.id}
                                            src={`${CATALOG_IMAGE_BUCKET}/thumb_${img.imageUrl}.jpg`}
                                            alt={`Thumb ${idx + 1}`}
                                            style={{
                                                width: 60,
                                                height: 60,
                                                objectFit: 'cover',
                                                borderRadius: 4,
                                                cursor: 'pointer',
                                                border: idx === imageIndex ? '2px solid #3366FF' : '2px solid transparent'
                                            }}
                                            onClick={() => this.setState({ imageIndex: idx })}
                                        />
                                    ))}
                                </Pane>
                            </Pane>
                        )}
                    </Pane>
                )}
            </Dialog>
        );
    }
}
